import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import UiUtils from "../../utils/ui-utils.ts";
import Route from "../../enums/route.ts";
import LogUtils from "../../utils/log-utils.ts";
import RestaurantPreference from "../../enums/restaurant-preference.ts";
import V1GroupRestaurantCategoriesChosen from "../../payloads/incoming/v1-group-restaurant-categories-chosen.ts";
import V1GroupJoined from "../../payloads/incoming/v1-group-joined.ts";
import SharedApiLogic from "../../api-client/shared-api-logic.ts";
import V1GroupReadied from "../../payloads/incoming/v1-group-readied.ts";

export default class RestaurantCategoriesScreen extends BaseUI {
  constructor(
    state: State,
    apiClient: EatOutApiClient,
    navigateCallback: (route: Route) => void,
  ) {
    super("restaurant-categories-screen", state, apiClient, navigateCallback);

    // subscribe to events
    this.apiClient.v1GroupJoinedCallback = this.handleV1GroupJoined.bind(this);
    this.apiClient.v1GroupReadiedCallback = this.handleV1GroupReadied.bind(this);
    this.apiClient.v1GroupRestaurantCategoriesChosenCallback = this.handleV1GroupRestaurantCategoriesChosenCallback
      .bind(this);
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // header title
    const headerParagraph: HTMLParagraphElement = UiUtils.paragraph(
      "Tap on restaurant categories to change your preference!",
    );
    newElements.push(headerParagraph);

    // create container for the list of categories
    const categoriesDiv: HTMLDivElement = UiUtils.div("eat-out-restaurant-categories");
    newElements.push(categoriesDiv);

    // make sure restaurant categories have been retrieved
    if (this.state.categories === null) {
      LogUtils.error(
        "attempted to render RestaurantCategories screen, but the restaurant categories have not been retrieved yet",
      );
    }

    // display all given categories
    for (const category of this.state.categories) {
      // convert category from snake-case to human-readable camel-case
      const categoryParts: string[] = category.split("_");
      categoryParts.forEach((element: string, index: number, array: string[]): void => {
        array[index] = element.charAt(0).toUpperCase() + element.slice(1);
      });
      const humanReadableCategory: string = categoryParts.join(" ");

      // create the UI button
      const categoryButton: HTMLButtonElement = UiUtils.button(
        humanReadableCategory,
        this.handleCategoryButtonClick.bind(this),
      );
      categoryButton.classList.remove(...categoryButton.classList); // remove all pre-set global button CSS
      categoryButton.classList.add("eat-out-restaurant-category"); // add CSS specific to these special buttons
      categoryButton.dataset.preference = RestaurantPreference.Neutral; // initialize the category preference to 'neutral'
      categoryButton.dataset.category = category; // store original 'snake_case' category ID for later
      categoriesDiv.appendChild(categoryButton);
    }

    // create submit button
    const submitButton: HTMLButtonElement = UiUtils.button(
      "Submit",
      this.handleSubmitRestaurantCategoryPreferences.bind(this),
    );
    newElements.push(submitButton);

    return super.render(newElements);
  }

  /**
   * When a category button is clicked, cycle between 'neutral' (0), 'likes' (1), and 'dislikes' (-1).
   */
  private handleCategoryButtonClick(event: MouseEvent): void {
    const eventTarget: EventTarget | null = event.target;
    if (!(eventTarget instanceof HTMLButtonElement)) {
      LogUtils.error(
        `attempted to modify restaurant category preference, but the event's target was a ${typeof eventTarget} when we expected an HTMLButtonElement`,
      );
    }

    // set new category preference
    const currentPreference: string | undefined = eventTarget.dataset.preference;
    if (currentPreference === undefined) {
      LogUtils.error(
        `attempted to retrieve restaurant category preference, but the preference was 'undefined'`,
      );
    }
    if (currentPreference === RestaurantPreference.Neutral) {
      eventTarget.dataset.preference = RestaurantPreference.Like;
    } else if (currentPreference === RestaurantPreference.Like) {
      eventTarget.dataset.preference = RestaurantPreference.Dislike;
    } else if (currentPreference === RestaurantPreference.Dislike) {
      eventTarget.dataset.preference = RestaurantPreference.Neutral;
    } else {
      LogUtils.error(
        `attempted to retrieve restaurant category preference (${eventTarget.innerText}), but the preference was configured to an invalid number: ${currentPreference}`,
      );
    }
  }

  private handleSubmitRestaurantCategoryPreferences(): void {
    // find the <div> which contains all the restaurant categories
    let categoriesDiv: HTMLElement | null = null;
    for (const child of this.container.getElementsByTagName("div")) {
      // found it!
      if (child.id === "eat-out-restaurant-categories") {
        categoriesDiv = child;
      }
    }

    // make sure we actually found it
    if (!(categoriesDiv instanceof HTMLDivElement)) {
      LogUtils.error(
        `attempted to gather restaurant category preferences, but the categories div was a ${typeof categoriesDiv} when we expected an HTMLDivElement`,
      );
    }

    const restaurantCategoryPreferences: Record<string, RestaurantPreference> = {};
    for (const child of categoriesDiv.children) {
      if (!(child instanceof HTMLButtonElement)) {
        LogUtils.error(
          `attempted to loop through all restaurant category buttons, but the child was a ${typeof child} when we expected an HTMLButtonElement`,
        );
      }

      // create a mapping of category -> preference
      const category: string | undefined = child.dataset.category;
      if (category === undefined) {
        LogUtils.error(
          `attempted to retrieve restaurant category's ID, but the ID was 'undefined'`,
        );
      }
      const preference: string | undefined = child.dataset.preference;
      if (preference === undefined) {
        LogUtils.error(
          `attempted to retrieve restaurant category preference, but the preference was 'undefined'`,
        );
      }
      if (preference === RestaurantPreference.Neutral) {
        restaurantCategoryPreferences[category] = RestaurantPreference.Neutral;
      } else if (preference === RestaurantPreference.Like) {
        restaurantCategoryPreferences[category] = RestaurantPreference.Like;
      } else if (preference === RestaurantPreference.Dislike) {
        restaurantCategoryPreferences[category] = RestaurantPreference.Dislike;
      } else {
        LogUtils.error(
          `attempted to retrieve restaurant category preference (${category}), but the preference was configured to an invalid number: ${preference}`,
        );
      }
    }

    // send payload
    this.apiClient.sendRestaurantCategoryPreferencesPayload(restaurantCategoryPreferences);
  }

  private handleV1GroupRestaurantCategoriesChosenCallback(
    v1GroupRestaurantCategoriesChosen: V1GroupRestaurantCategoriesChosen,
  ): void {
    SharedApiLogic.handleV1GroupRestaurantCategoriesChosenCallback(
      this.debug,
      this.state,
      v1GroupRestaurantCategoriesChosen,
    );

    // only change screen if the user that chose these Restaurant Category Preferences matches this client
    if (v1GroupRestaurantCategoriesChosen.payload.user_id === this.state.userState.id) {
      LogUtils.debug(this.debug, "Successfully submitted restaurant category preferences.");

      // now that we've sent our choices, wait for everyone else to finish choosing
      this.navigateCallback(Route.RESTAURANT_CATEGORIES_LOBBY);
    }
  }

  private handleV1GroupJoined(v1GroupJoined: V1GroupJoined): void {
    SharedApiLogic.handleV1GroupJoined(this.state, v1GroupJoined);
  }

  private handleV1GroupReadied(v1GroupReadied: V1GroupReadied): void {
    SharedApiLogic.handleV1GroupReadied(this.state, v1GroupReadied);
  }
}
